import { useEffect, useRef, useState } from 'react';
import { HomeBackgroundService } from '../services/HomeBackground/HomeBackgroundService';
import { HomeBackgroundModel } from '../models/HomeBackground/HomeBackgroundModel';

const LAST_BACKGROUND_KEY = '@beviajuda:home-background-last-id';

const readLastId = (): number | null => {
    try {
        const value = sessionStorage.getItem(LAST_BACKGROUND_KEY);
        return value ? Number(value) : null;
    } catch {
        return null;
    }
};

const saveLastId = (id: number) => {
    try {
        sessionStorage.setItem(LAST_BACKGROUND_KEY, String(id));
    } catch {
        // sessionStorage indisponível: apenas não evita a repetição
    }
};

export const pickRandomBackground = (
    items: HomeBackgroundModel[],
    lastId: number | null,
    random: () => number = Math.random,
): HomeBackgroundModel | null => {
    if (!items.length) {
        return null;
    }

    const candidates = items.length > 1 ? items.filter((item) => item.id !== lastId) : items;

    return candidates[Math.floor(random() * candidates.length)];
};

export const resolveBackgroundUrl = (imageUrl?: string): string | null => {
    if (!imageUrl) {
        return null;
    }

    return imageUrl.startsWith('/') ? `${process.env.PUBLIC_URL}${imageUrl}` : imageUrl;
};

const fetchActiveBackgrounds = async (): Promise<HomeBackgroundModel[]> => {
    try {
        const [response, error] = await new HomeBackgroundService().listActive();
        return error ? [] : response?.items ?? [];
    } catch {
        return [];
    }
};

let prefetched: Promise<HomeBackgroundModel[]> | null = null;

export const prefetchHomeBackgrounds = () => {
    if (!prefetched) {
        prefetched = fetchActiveBackgrounds();
    }
};

const takeActiveBackgrounds = (): Promise<HomeBackgroundModel[]> => {
    const request = prefetched ?? fetchActiveBackgrounds();
    prefetched = null;
    return request;
};

let loadedBackgrounds: HomeBackgroundModel[] | null = null;

const chooseBackgroundUrl = (items: HomeBackgroundModel[]): string | null => {
    const chosen = pickRandomBackground(items, readLastId());
    if (chosen?.id) {
        saveLastId(chosen.id);
    }

    return resolveBackgroundUrl(chosen?.image_url);
};

export const useHomeBackground = (enabled: boolean = true): string | null | undefined => {
    const [backgroundUrl, setBackgroundUrl] = useState<string | null | undefined>(() =>
        enabled && loadedBackgrounds?.length ? chooseBackgroundUrl(loadedBackgrounds) : undefined,
    );
    const hasBackground = useRef(backgroundUrl !== undefined);

    useEffect(() => {
        if (!enabled) {
            return;
        }

        let cancelled = false;

        takeActiveBackgrounds().then((items) => {
            if (items.length) {
                loadedBackgrounds = items;
            }

            if (cancelled || hasBackground.current) {
                return;
            }

            hasBackground.current = true;
            setBackgroundUrl(chooseBackgroundUrl(items));
        });

        return () => {
            cancelled = true;
        };
    }, [enabled]);

    return backgroundUrl;
};
